// PrivilegesStore
import { defineStore } from 'pinia'
import { computed, ref } from 'vue';
import * as ChoiceApi from '../ChoiceApi'
import { PrivilegeType, AppScreens } from '../../Enums/ChoiceEnums';

export const usePrivilegesStore = defineStore('PrivilegesStore', () => {
    const screens = ref<Record<string, PrivilegeType>>({no_priv:0, read_priv:1, update_priv:2,write_priv:3});

    function screensWithPrivilege(privilegeType:PrivilegeType){
        const hasReadPrivilege: Record<string, boolean> = {}
        for(const key  of Object.keys(screens.value)){
            hasReadPrivilege[key] = screens.value[key] >= privilegeType
        }
        return hasReadPrivilege
    }

    const screensWithReadPrivileges = computed(() => screensWithPrivilege(PrivilegeType.Read))
    const screensWithUpdatePrivileges = computed(() => screensWithPrivilege(PrivilegeType.ReadUpdate))
    const screensWithWritePrivileges = computed(() => screensWithPrivilege(PrivilegeType.ReadUpdateWrite))


    // Get the user privileges for a screen
    //  Return ALL screen privileges we've gotten up to now
    async function getScreens(screenNames: AppScreens[]): Promise<Record<string, PrivilegeType>> {
        // See if we've gotten all priveledge info for the specified screens
        const neededScreens = screenNames.filter(screenName => !screens.value[screenName])
        if (neededScreens?.length > 0)
        {
            // Fetch from API
            const values = await ChoiceApi.user.getMultipleScreenPrivileges(neededScreens);
            if (values?.length !== neededScreens.length)
            {
                alert(`Wrong number of screen privilege values returned: ${values?.length ?? 0} (expected ${screenNames.length})`);
            }
            for (const item of values ?? [])
            {
                screens.value[item.screenName] = item.privilege;
            }
        }
        return screens.value;
    }

    // Get the user privileges for a screen
    async function getScreen(screenName: AppScreens): Promise<PrivilegeType> {
        const currentVal = screens.value[screenName];
        if (undefined !== currentVal)
            return currentVal;

        // Fetch from API
        const fetchedVal = await ChoiceApi.user.screenPrivilege(screenName);
        screens.value[screenName] = fetchedVal;
        return fetchedVal;
    }

    function clearScreens(): void {
        screens.value = {};
    }

    // Make sure the user meets the minimum privilege
    async function allow(screen: AppScreens, minPriv: PrivilegeType): Promise<boolean>
    {
        const priv = await getScreen(screen);
        return (priv >= minPriv);
    }

    // Is the screen allowed to perform read/views?
    async function allowRead(screen: AppScreens): Promise<boolean | undefined>
    {
        return await allow(screen, PrivilegeType.Read);
    }

    /* Allow read/modify/create rights */
    async function allowAll(screen: AppScreens): Promise<boolean | undefined>
    {
        return allow(screen, PrivilegeType.ReadUpdateWrite);
    }


    return { 
        screens
        , getScreen
        , getScreens
        , clearScreens
        , allow
        , allowRead
        , allowAll
        , screensWithReadPrivileges
        , screensWithUpdatePrivileges
        , screensWithWritePrivileges
     };
});


